import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { Contact, ContactFilters } from "@/types/contact";
import type { UseBulkSelectionReturn } from "@/hooks/useBulkSelection";
import { Table } from "@tanstack/react-table";

import { CheckCircle2, X } from "lucide-react";

interface ContactTableToolbarProps {
  table: Table<Contact>;
  bulkSelection: UseBulkSelectionReturn;
  totalCount: number;
  currentFilters: ContactFilters;
}

export function ContactTableToolbar({
  table,
  bulkSelection,
  totalCount,
  currentFilters,
}: ContactTableToolbarProps) {
  const selectedRows = table.getFilteredSelectedRowModel().rows;

  // Use effective count from bulk selection (handles both explicit and all_matching modes)
  const selectedCount = bulkSelection.effectiveCount;

  // Determine if we should show the "Select All X Results" banner
  // Show when: in explicit mode AND all visible rows are selected AND there are more total results
  const visibleRowCount = table.getRowModel().rows.length;
  const allVisibleSelected = selectedRows.length === visibleRowCount && visibleRowCount > 0;
  const hasMoreResults = totalCount > visibleRowCount;
  const showSelectAllBanner =
    bulkSelection.mode === 'explicit' &&
    allVisibleSelected &&
    hasMoreResults;

  // Handle "Select All X Results" click
  const handleSelectAllResults = () => {
    bulkSelection.selectAll(currentFilters, totalCount);
  };

  // Build selection description for display
  const getSelectionDescription = () => {
    if (bulkSelection.mode === 'all_matching') {
      const excluded = bulkSelection.excludedIds.size;
      if (excluded > 0) {
        return `All ${totalCount.toLocaleString()} contacts selected (${excluded} excluded)`;
      }
      return `All ${totalCount.toLocaleString()} contacts selected`;
    }
    return `${selectedCount} selected`;
  };

  return (
    <div className="space-y-2">
      {/* Select All Results Banner */}
      {showSelectAllBanner && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-primary/10 border border-primary/20 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-primary" />
          <span className="text-sm">
            All {visibleRowCount} contacts on this page are selected.
          </span>
          <Button
            variant="link"
            size="sm"
            className="text-primary font-medium p-0 h-auto"
            onClick={handleSelectAllResults}
          >
            Select all {totalCount.toLocaleString()} results
          </Button>
        </div>
      )}

      {/* All Matching Mode Banner */}
      {bulkSelection.mode === 'all_matching' && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-700">
            {getSelectionDescription()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground p-1 h-auto"
            onClick={() => bulkSelection.clearSelection()}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Clear selection</span>
          </Button>
        </div>
      )}

      {/* Selection count badge (no buttons) */}
      {selectedCount > 0 && bulkSelection.mode !== 'all_matching' && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="rounded-lg">
            {getSelectionDescription()}
          </Badge>
        </div>
      )}
    </div>
  );
}
